import React, { useState, useEffect, useRef } from 'react'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useLangPrefix } from '../../hooks/useLangPrefix'
import useTenantVertical from '../../hooks/useTenantVertical'
import { useTenantCurrencyState } from '../../hooks/useTenantCurrency'
import { DEFAULT_CURRENCY, getMinorUnits } from '../../lib/money'
import { getKbDocuments, deleteKbDocument, ingestKbUrl, uploadKbFile } from '../../api/tenant'
import api from '../../lib/api'

const cardStyle = { background: 'white', border: '0.5px solid #e0e3f0', borderRadius: 12, padding: 18, marginBottom: 12 }
const inputStyle = { width: '100%', padding: '8px 12px', border: '0.5px solid #e0e3f0', borderRadius: 8, fontSize: 13, background: 'white', color: '#111', outline: 'none', boxSizing: 'border-box' }
const directPromptEditStyle = { ...inputStyle, whiteSpace: 'pre-wrap', wordBreak: 'break-word', minHeight: 80, resize: 'vertical', fontFamily: 'inherit' }
const labelStyle = { fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4, display: 'block' }

// Structured starter template seeded into an EMPTY prompt so operators author
// the bot's instructions like a system prompt. Business/policy level only —
// booking mechanics, slot order, and confirmation gating live in the backend
// scaffold and must NOT be duplicated here.
const PROMPT_TEMPLATES = {
  fnb: {
    en: `# IDENTITY
You are [bot name], the assistant for [business name]. You speak in a [friendly / professional] manner.

# SCOPE
You help customers with: [bookings / orders / product questions / hours & location].
You do NOT handle: [refunds / complaints requiring a manager / anything not listed above].

# PROCESS
- Greet the customer and find out what they need.
- For bookings, collect the required details one at a time.
- Recommend [signature items / set menu] when appropriate.
- If you cannot help after 2 tries, offer to connect a human.

# OUTPUT FORMAT
- Keep replies short and natural for chat.
- Reply in the customer's language.
- Show prices in {{currency}} {{priceRule}} (e.g. {{priceExample}}).

# EDGE CASES
- Bookings after closing time: [your rule].
- Fully booked: [your rule].
- Customer upset: apologize and offer human handoff.`,
    vi: `# NHẬN DIỆN (IDENTITY)
Bạn là [tên bot], trợ lý của [tên doanh nghiệp]. Bạn giao tiếp theo phong cách [thân thiện / chuyên nghiệp].

# PHẠM VI (SCOPE)
Bạn hỗ trợ khách: [đặt bàn / đặt hàng / hỏi sản phẩm / giờ mở cửa & địa chỉ].
Bạn KHÔNG xử lý: [hoàn tiền / khiếu nại cần quản lý / những việc không liệt kê ở trên].

# QUY TRÌNH (PROCESS)
- Chào khách và tìm hiểu nhu cầu.
- Với đặt bàn, thu thập từng thông tin một, không hỏi dồn.
- Gợi ý [món đặc trưng / set menu] khi phù hợp.
- Nếu sau 2 lần vẫn không hỗ trợ được, đề nghị chuyển cho nhân viên.

# ĐỊNH DẠNG TRẢ LỜI (OUTPUT FORMAT)
- Trả lời ngắn gọn, tự nhiên như tin nhắn.
- Trả lời bằng ngôn ngữ của khách.
- Hiển thị giá bằng {{currency}} {{priceRule}} (vd: {{priceExample}}).

# TRƯỜNG HỢP ĐẶC BIỆT (EDGE CASES)
- Đặt bàn sau giờ đóng cửa: [quy định của bạn].
- Hết chỗ: [quy định của bạn].
- Khách không hài lòng: xin lỗi và đề nghị chuyển nhân viên.`,
  },
  spa: {
    en: `# IDENTITY
You are [bot name], the assistant for [spa name]. You speak in a [warm / professional] manner.

# SCOPE
You help clients with: [booking treatments / service & pricing questions / hours & location].
You do NOT handle: [medical advice / diagnosis / refunds / anything not listed above].

# PROCESS
- Greet the client and find out what they need.
- For appointments, collect which service they want, then the remaining details one at a time.
- Recommend [signature treatments / packages] by benefit and duration when appropriate.
- If the client mentions pregnancy, allergies, or a health condition, note it for staff — never advise.
- If you cannot help after 2 tries, offer to connect a human.

# OUTPUT FORMAT
- Keep replies short and natural for chat.
- Reply in the client's language.
- Show prices in {{currency}} {{priceRule}} (e.g. {{priceExample}}).

# EDGE CASES
- Requests after closing time: [your rule].
- Preferred therapist unavailable: [your rule].
- Client upset: apologize and offer human handoff.`,
    vi: `# NHẬN DIỆN (IDENTITY)
Bạn là [tên bot], trợ lý của [tên spa]. Bạn giao tiếp theo phong cách [ấm áp / chuyên nghiệp].

# PHẠM VI (SCOPE)
Bạn hỗ trợ khách: [đặt lịch liệu trình / hỏi dịch vụ & giá / giờ mở cửa & địa chỉ].
Bạn KHÔNG xử lý: [tư vấn y khoa / chẩn đoán / hoàn tiền / những việc không liệt kê ở trên].

# QUY TRÌNH (PROCESS)
- Chào khách và tìm hiểu nhu cầu.
- Với lịch hẹn, hỏi khách muốn dịch vụ/liệu trình nào trước, sau đó thu thập từng thông tin một.
- Gợi ý [liệu trình đặc trưng / gói dịch vụ] theo lợi ích và thời lượng khi phù hợp.
- Nếu khách nhắc đến mang thai, dị ứng, hay vấn đề sức khoẻ: ghi chú cho nhân viên — tuyệt đối không tư vấn.
- Nếu sau 2 lần vẫn không hỗ trợ được, đề nghị chuyển cho nhân viên.

# ĐỊNH DẠNG TRẢ LỜI (OUTPUT FORMAT)
- Trả lời ngắn gọn, tự nhiên như tin nhắn.
- Trả lời bằng ngôn ngữ của khách.
- Hiển thị giá bằng {{currency}} {{priceRule}} (vd: {{priceExample}}).

# TRƯỜNG HỢP ĐẶC BIỆT (EDGE CASES)
- Đặt lịch sau giờ đóng cửa: [quy định của bạn].
- Kỹ thuật viên khách yêu cầu bận: [quy định của bạn].
- Khách không hài lòng: xin lỗi và đề nghị chuyển nhân viên.`,
  },
  education: {
    en: `# IDENTITY
You are [bot name], the assistant for [center name]. You speak in a [friendly / professional] manner.

# SCOPE
You help students and parents with: [course information / schedules & tuition / trial classes / enrollment].
You do NOT handle: [guaranteeing scores or outcomes / refunds / anything not listed above].

# PROCESS
- Greet and find out who is asking (student or parent) and what they need.
- For enrollment, ask which course they're interested in, then collect the remaining details one at a time.
- If a parent enrolls a child, collect the student's name separately.
- Recommend [flagship courses] by level, goal, and schedule fit when appropriate.
- If you cannot help after 2 tries, offer to connect a human.

# OUTPUT FORMAT
- Keep replies short, encouraging, and natural for chat.
- Reply in the customer's language.
- Show tuition in {{currency}} {{priceRule}} (e.g. {{priceExample}}).

# EDGE CASES
- Class is full: [your rule].
- Asking about outcomes/certificates: state only what is published — never promise results.
- Customer upset: apologize and offer human handoff.`,
    vi: `# NHẬN DIỆN (IDENTITY)
Bạn là [tên bot], trợ lý của [tên trung tâm]. Bạn giao tiếp theo phong cách [thân thiện / chuyên nghiệp].

# PHẠM VI (SCOPE)
Bạn hỗ trợ học viên và phụ huynh: [thông tin khoá học / lịch học & học phí / học thử / đăng ký học].
Bạn KHÔNG xử lý: [cam kết điểm số hay kết quả / hoàn tiền / những việc không liệt kê ở trên].

# QUY TRÌNH (PROCESS)
- Chào và tìm hiểu người nhắn là học viên hay phụ huynh, và nhu cầu là gì.
- Với đăng ký học, hỏi khách quan tâm khoá nào trước, sau đó thu thập từng thông tin một.
- Nếu phụ huynh đăng ký cho con: hỏi riêng tên học viên.
- Gợi ý [khoá học tiêu biểu] theo trình độ, mục tiêu và lịch học khi phù hợp.
- Nếu sau 2 lần vẫn không hỗ trợ được, đề nghị chuyển cho nhân viên.

# ĐỊNH DẠNG TRẢ LỜI (OUTPUT FORMAT)
- Trả lời ngắn gọn, khích lệ, tự nhiên như tin nhắn.
- Trả lời bằng ngôn ngữ của khách.
- Hiển thị học phí bằng {{currency}} {{priceRule}} (vd: {{priceExample}}).

# TRƯỜNG HỢP ĐẶC BIỆT (EDGE CASES)
- Lớp đã đầy: [quy định của bạn].
- Hỏi về kết quả/chứng chỉ: chỉ nêu thông tin đã công bố — không hứa hẹn kết quả.
- Khách không hài lòng: xin lỗi và đề nghị chuyển nhân viên.`,
  },
}

// The seven document types (RAG rework). The URL-ingest path stores its pages as
// 'general' on its own; the backend maps the retired values (web_crawl, other,
// user_story, workflow, service) to these when it reads old documents.
const KB_DOC_TYPES = ['faq', 'menu', 'policy', 'ai_character', 'promo', 'sop', 'general']
// Must match SUPPORTED_KB_FILE_EXTENSIONS in the backend (kb_file_extraction.py).
const KB_FILE_ACCEPT = '.txt,.md,.csv,.doc,.docx,.pdf'
const KB_ALLOWED_EXTS = KB_FILE_ACCEPT.split(',')
// Single source for the upload size cap shown in the help text and enforced client-side.
const KB_MAX_FILE_MB = 5
const KB_MAX_FILE_BYTES = KB_MAX_FILE_MB * 1024 * 1024

// Last extension, lowercased; mirrors the backend's rsplit('.', 1) logic.
function fileExt(name) {
  const i = name.lastIndexOf('.')
  return i >= 0 ? name.slice(i).toLowerCase() : ''
}

function titleFromFileName(name) {
  const i = name.lastIndexOf('.')
  return i > 0 ? name.slice(0, i) : name
}

// Returns an i18n error key (with params) for an invalid file, or null when acceptable.
function validateKbFile(file) {
  if (!KB_ALLOWED_EXTS.includes(fileExt(file.name))) return { key: 'chatKnowledge.uploadInvalidType', params: { formats: KB_ALLOWED_EXTS.join(', ') } }
  if (file.size === 0) return { key: 'chatKnowledge.uploadEmptyFile' }
  if (file.size > KB_MAX_FILE_BYTES) return { key: 'chatKnowledge.uploadTooLarge', params: { maxMb: KB_MAX_FILE_MB } }
  return null
}

// Example prices shown in the template, in the bot's "<amount> <CODE>" output
// format (SPEC-growchat-tenant-currency.md: `150,000 VND`, `12.99 CAD`).
// Hand-picked per currency rather than converted: amounts are never FX-converted.
const PRICE_EXAMPLES = {
  fnb: { VND: '150,000', CAD: '12.50', USD: '12.50' },
  spa: { VND: '500,000', CAD: '45.00', USD: '45.00' },
  education: { VND: '2,800,000', CAD: '250.00', USD: '250.00' },
}

// How the bot should write amounts: grouping only (0 decimals) or 2 decimals.
const PRICE_RULES = {
  en: { whole: 'with commas', decimals: 'with commas and 2 decimals' },
  vi: { whole: 'có dấu phẩy', decimals: 'có dấu phẩy và 2 chữ số thập phân' },
}

// Verticals without their own template (hotel/retail/other) fall back to the
// fnb template.
function getPromptTemplate(vertical, lang, currency) {
  const key = PROMPT_TEMPLATES[vertical] ? vertical : 'fnb'
  const set = PROMPT_TEMPLATES[key]
  const template = set[lang] || set.en
  const examples = PRICE_EXAMPLES[key]
  const code = examples[currency] ? currency : DEFAULT_CURRENCY
  const rules = PRICE_RULES[set[lang] ? lang : 'en']
  return template
    .replaceAll('{{currency}}', code)
    .replaceAll('{{priceRule}}', getMinorUnits(code) > 0 ? rules.decimals : rules.whole)
    .replaceAll('{{priceExample}}', `${examples[code]} ${code}`)
}

function pickDirectPrompt(record) {
  if (!record || typeof record !== 'object') return ''
  return record.direct_prompt ?? record.directPrompt ?? ''
}

export default function ChatKnowledge({ tenantId, bot }) {
  const { t, i18n } = useTranslation()
  const { prefix } = useLangPrefix()
  const vertical = useTenantVertical()
  const [docs, setDocs] = useState([])
  const [url, setUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [ingesting, setIngesting] = useState(false)
  const [uploadFile, setUploadFile] = useState(null)
  const [uploadTitle, setUploadTitle] = useState('')
  const [uploadDocType, setUploadDocType] = useState(KB_DOC_TYPES[0])
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const fileInputRef = useRef(null)
  // Last title we auto-filled from a file name; lets us replace it on a new pick
  // without ever clobbering a title the user typed.
  const autoTitleRef = useRef('')
  const [directPrompt, setDirectPrompt] = useState('')
  const [savingPrompt, setSavingPrompt] = useState(false)
  const [promptSaved, setPromptSaved] = useState(false)
  const [promptError, setPromptError] = useState('')
  const [isSeeded, setIsSeeded] = useState(false)
  // True while the bot has no saved prompt and the template is not seeded yet
  // (e.g. tenant currency still resolving).
  const [awaitingSeed, setAwaitingSeed] = useState(false)
  const { currency, isResolved, isError: currencyError } = useTenantCurrencyState()

  useEffect(() => { loadDocs() }, [tenantId])
  // Initial load: use the saved prompt, or queue the structured template if empty.
  useEffect(() => {
    const saved = pickDirectPrompt(bot)
    if (saved && saved.trim()) {
      setDirectPrompt(saved)
      setIsSeeded(false)
      setAwaitingSeed(false)
    } else {
      setDirectPrompt('')
      setIsSeeded(false)
      setAwaitingSeed(true)
    }
  }, [bot])

  // Seed only once the tenant currency is resolved, so a CAD tenant never gets
  // a VND template. Re-seed on language, currency or vertical change only while
  // the template is still pristine (not typed in, not saved). useTenantVertical
  // returns 'fnb' until its lookup finishes and exposes no resolved flag, so a
  // late vertical is corrected by this pristine re-seed.
  useEffect(() => {
    if (!isResolved || !(awaitingSeed || isSeeded)) return
    const lang = (i18n.language || 'en').slice(0, 2)
    setDirectPrompt(getPromptTemplate(vertical, lang, currency))
    setIsSeeded(true)
    setAwaitingSeed(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingSeed, isResolved, currency, vertical, i18n.language])

  async function loadDocs() {
    setLoading(true)
    try {
      const data = await getKbDocuments(tenantId, 'chatizi')
      setDocs(data?.documents ?? data ?? [])
    } catch (err) {
      Sentry.captureException(err, { tags: { action: 'load_kb_docs_chat' }, extra: { tenantId } })
    }
    setLoading(false)
  }

  async function handleSavePrompt() {
    setSavingPrompt(true)
    setPromptError('')
    try {
      await api.patch(`/admin/tenants/${tenantId}`, { direct_prompt: directPrompt })
      setPromptSaved(true)
      // A saved template is the operator's prompt now: never re-seed it.
      setIsSeeded(false)
      setAwaitingSeed(false)
      api.post(`/admin/kb/retrain/${tenantId}?bot_type=chatizi`).catch(() => { })
      setTimeout(() => setPromptSaved(false), 2000)
      await loadDocs()
    } catch (err) {
      if (!err?.response || err.response.status >= 500)
        Sentry.captureException(err, { tags: { action: 'save_direct_prompt' }, extra: { tenantId } })
      setPromptError(err?.response?.data?.message || err?.message || 'Error')
    }
    setSavingPrompt(false)
  }

  async function handleIngest() {
    if (!url.trim()) return
    setIngesting(true)
    try {
      await ingestKbUrl(tenantId, url.trim(), 'chatizi')
      setUrl('')
      await loadDocs()
    } catch (err) {
      Sentry.captureException(err, { tags: { action: 'ingest_kb_url_chat' }, extra: { tenantId, url } })
    }
    setIngesting(false)
  }

  function resetFileInput() {
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  function handleFileChange(e) {
    const file = e.target.files?.[0] || null
    setUploadError('')
    if (!file) { setUploadFile(null); return }
    const invalid = validateKbFile(file)
    if (invalid) {
      setUploadFile(null)
      resetFileInput()
      setUploadError(t(invalid.key, invalid.params))
      return
    }
    setUploadFile(file)
    if (!uploadTitle.trim() || uploadTitle === autoTitleRef.current) {
      const auto = titleFromFileName(file.name)
      autoTitleRef.current = auto
      setUploadTitle(auto)
    }
  }

  async function handleUpload() {
    if (!uploadFile || !uploadTitle.trim()) return
    const invalid = validateKbFile(uploadFile)
    if (invalid) { setUploadError(t(invalid.key, invalid.params)); return }
    setUploading(true)
    setUploadError('')
    try {
      await uploadKbFile(tenantId, { file: uploadFile, docType: uploadDocType, title: uploadTitle.trim(), botType: 'chatizi' })
      setUploadFile(null)
      setUploadTitle('')
      autoTitleRef.current = ''
      resetFileInput()
      await loadDocs()
    } catch (err) {
      if (!err?.response || err.response.status >= 500)
        Sentry.captureException(err, { tags: { action: 'upload_kb_file_chat' }, extra: { tenantId } })
      setUploadError(err?.response?.data?.detail || err?.message || t('chatKnowledge.uploadError'))
    }
    setUploading(false)
  }

  async function handleDelete(docId) {
    try {
      await deleteKbDocument(docId)
      setDocs(prev => prev.filter(d => d.id !== docId))
    } catch (err) {
      Sentry.captureException(err, { tags: { action: 'delete_kb_doc_chat' }, extra: { tenantId, docId } })
    }
  }

  const showTitleHint = Boolean(uploadFile) && !uploadTitle.trim()

  return (
    <div>
      <div style={cardStyle}>
        <label style={labelStyle}>{t('chatKnowledge.directPrompt')}</label>
        <p style={{ fontSize: 11, color: '#888', margin: '0 0 8px' }}>{t('chatKnowledge.directPromptHelp')}</p>
        {isSeeded && (
          <p style={{ fontSize: 11, color: '#b45309', background: '#fffbeb', border: '0.5px solid #fde68a', borderRadius: 6, padding: '6px 10px', margin: '0 0 8px' }}>
            {t('chatKnowledge.seededHint')}
          </p>
        )}
        {awaitingSeed && !isResolved && currencyError && (
          <p role="status" style={{ fontSize: 11, color: '#b45309', margin: '0 0 8px' }}>
            {t('chatKnowledge.templateUnavailable')}
          </p>
        )}
        <textarea
          style={directPromptEditStyle}
          value={directPrompt}
          onChange={e => { setDirectPrompt(e.target.value); setIsSeeded(false); setAwaitingSeed(false) }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
          <button onClick={handleSavePrompt} disabled={savingPrompt}
            style={{ background: promptSaved ? '#16a34a' : '#1535c8', color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: savingPrompt ? 0.6 : 1, whiteSpace: 'nowrap' }}>
            {savingPrompt ? t('common.saving') : promptSaved ? t('common.saved') : t('common.saveChanges')}
          </button>
          {promptError && <span style={{ color: '#ef4444', fontSize: 12 }}>{promptError}</span>}
        </div>
      </div>

      <div style={cardStyle}>
        <label style={labelStyle}>{t('chatKnowledge.addUrl')}</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={{ ...inputStyle, flex: 1 }} value={url} onChange={e => setUrl(e.target.value)}
            placeholder={t('chatKnowledge.urlPh')}
            onKeyDown={e => e.key === 'Enter' && handleIngest()} />
          <button onClick={handleIngest} disabled={ingesting || !url.trim()}
            style={{ background: '#1535c8', color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (ingesting || !url.trim()) ? 0.6 : 1, whiteSpace: 'nowrap' }}>
            {ingesting ? t('chatKnowledge.adding') : t('chatKnowledge.addUrlBtn')}
          </button>
        </div>
      </div>

      <div style={cardStyle}>
        <label style={labelStyle}>{t('chatKnowledge.uploadFile')}</label>
        <p style={{ fontSize: 11, color: '#888', margin: '0 0 8px' }}>{t('chatKnowledge.uploadFileHelp', { formats: KB_ALLOWED_EXTS.join(', '), maxMb: KB_MAX_FILE_MB })}</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <select style={{ ...inputStyle, width: 'auto', flex: '0 0 auto' }} value={uploadDocType}
            onChange={e => setUploadDocType(e.target.value)}>
            {KB_DOC_TYPES.map(dt => (
              <option key={dt} value={dt}>{t(`chatKnowledge.docType.${dt}`)}</option>
            ))}
          </select>
          <input style={{ ...inputStyle, flex: 1, minWidth: 160 }} value={uploadTitle}
            onChange={e => setUploadTitle(e.target.value)}
            aria-label={t('chatKnowledge.uploadTitlePh')} aria-required="true" required
            aria-invalid={showTitleHint || undefined}
            aria-describedby={showTitleHint ? 'kb-upload-title-hint' : undefined}
            placeholder={`${t('chatKnowledge.uploadTitlePh')} *`} />
          <label style={{ ...inputStyle, flex: '0 0 auto', width: 'auto', cursor: 'pointer', color: uploadFile ? '#111' : '#888' }}>
            {uploadFile ? uploadFile.name : t('chatKnowledge.uploadChooseFile')}
            <input ref={fileInputRef} type="file" accept={KB_FILE_ACCEPT} style={{ display: 'none' }}
              data-testid="kb-file-input" onChange={handleFileChange}
              aria-describedby={uploadError ? 'kb-upload-error' : undefined} />
          </label>
          <button onClick={handleUpload} disabled={uploading || !uploadFile || !uploadTitle.trim()}
            style={{ background: '#1535c8', color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (uploading || !uploadFile || !uploadTitle.trim()) ? 0.6 : 1, whiteSpace: 'nowrap' }}>
            {uploading ? t('chatKnowledge.uploading') : t('chatKnowledge.uploadBtn')}
          </button>
        </div>
        {uploadDocType === 'faq' && (
          <p data-testid="kb-faq-hint" style={{ fontSize: 12, color: '#555', margin: '8px 0 0' }}>
            <Link to={`${prefix}/faq`} style={{ color: '#1535c8', fontWeight: 600 }}>{t('chatKnowledge.faqHint.line1')}</Link>
            {' '}{t('chatKnowledge.faqHint.line2')}
          </p>
        )}
        {showTitleHint && (
          <p id="kb-upload-title-hint" aria-live="polite" style={{ color: '#b45309', fontSize: 12, marginTop: 8 }}>{t('chatKnowledge.uploadTitleRequired')}</p>
        )}
        {uploadError && <p id="kb-upload-error" role="alert" style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>{uploadError}</p>}
      </div>

      <div style={cardStyle}>
        <p style={{ fontSize: 13, fontWeight: 600, color: '#111', marginBottom: 12 }}>{t('chatKnowledge.documents', { count: docs.length })}</p>
        {loading ? (
          <p style={{ fontSize: 13, color: '#888' }}>{t('chatKnowledge.loading')}</p>
        ) : docs.length === 0 ? (
          <p style={{ fontSize: 13, color: '#888' }}>{t('chatKnowledge.empty')}</p>
        ) : (
          <div>
            {docs.map(doc => (
              <div key={doc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '0.5px solid #f0f0f0' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 500, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.title || doc.url || doc.id}</p>
                  {doc.url && <p style={{ fontSize: 11, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.url}</p>}
                </div>
                <button onClick={() => handleDelete(doc.id)}
                  style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 12, cursor: 'pointer', marginLeft: 12, padding: '4px 8px' }}>
                  {t('chatKnowledge.delete')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
